import jsPDF from 'jspdf';

interface WebinarTraining {
  title: string;
  subtitle: string;
  description: string[];
  format: string;
  audience: string;
  date: string;
}

let logoBase64: string | null = null;
const loadLogo = (): Promise<string> => {
  if (logoBase64) return Promise.resolve(logoBase64);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      logoBase64 = canvas.toDataURL('image/png');
      resolve(logoBase64);
    };
    img.onerror = () => resolve('');
    img.src = '/gaius-logo.webp';
  });
};

export async function generateWebinarPDF(training: WebinarTraining, lang: 'fr' | 'en') {
  const fr = lang === 'fr';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = 20;

  const logoData = await loadLogo();

  const navy = [30, 41, 59] as const;
  const slate = [51, 65, 85] as const;
  const muted = [100, 116, 139] as const;
  const accent = [59, 130, 246] as const;
  const white = [255, 255, 255] as const;

  // Footer
  const addPageFooter = () => {
    const footerY = pageHeight - 12;
    doc.setDrawColor(203, 213, 225);
    doc.line(margin, footerY - 4, pageWidth - margin, footerY - 4);
    doc.setFontSize(6.5);
    doc.setTextColor(...muted);
    doc.setFont('helvetica', 'normal');
    doc.setCharSpace(0);
    doc.text('Gaius — Formation IA Juridique — gaius-tech.com — formation@gaius-tech.com', margin, footerY);
    doc.text(fr ? 'Organisme certifie Qualiopi' : 'Qualiopi-certified organization', pageWidth - margin, footerY, { align: 'right' });
  };

  // ── Header ──
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageWidth, 48, 'F');

  if (logoData) {
    try { doc.addImage(logoData, 'PNG', margin, 6, 12, 12); } catch { /* fallback */ }
  }

  doc.setTextColor(...white);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setCharSpace(0);
  doc.text('GAIUS', margin + (logoData ? 15 : 0), 14);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(fr ? 'Formation IA Juridique' : 'Legal AI Training', margin + (logoData ? 15 : 0), 19);

  // Badges top right
  doc.setFontSize(7);
  doc.setTextColor(180, 200, 255);
  doc.text('Qualiopi - OPCO - FIF-PL - CPF', pageWidth - margin, 14, { align: 'right' });
  doc.text('gaius-tech.com', pageWidth - margin, 19, { align: 'right' });

  // Title
  doc.setTextColor(...white);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setCharSpace(0);
  const titleLines = doc.splitTextToSize(training.title, contentWidth);
  doc.text(titleLines, margin, 32);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(doc.splitTextToSize(training.subtitle, contentWidth), margin, 42);
  y = 56;

  doc.setTextColor(...navy);
  doc.setCharSpace(0);

  // ── Section helper ──
  const addSection = (title: string) => {
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, contentWidth, 8, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...navy);
    doc.setCharSpace(0);
    doc.text(title.toUpperCase(), margin + 3, y + 5.5);
    doc.setFont('helvetica', 'normal');
    y += 12;
  };

  // ── Informations pratiques ──
  addSection(fr ? 'Informations pratiques' : 'Practical Information');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);

  const infoItems = [
    { label: fr ? 'Format' : 'Format', value: training.format },
    { label: fr ? 'Date' : 'Date', value: training.date },
    { label: fr ? 'Public cible' : 'Target audience', value: training.audience },
    { label: fr ? 'Nombre de participants' : 'Participants', value: fr ? '20 maximum' : '20 max' },
    { label: fr ? 'Tarif' : 'Price', value: '199,99 EUR HT / ' + (fr ? 'participant' : 'participant') },
    { label: fr ? 'Materiel requis' : 'Required equipment', value: fr ? 'Ordinateur avec connexion internet, navigateur web recent' : 'Computer with internet, modern browser' },
    { label: fr ? 'Attestation' : 'Certificate', value: fr ? 'Attestation de formation delivree a l\'issue de la session' : 'Training certificate issued after the session' },
  ];

  infoItems.forEach(item => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${item.label} :`, margin + 2, y);
    doc.setFont('helvetica', 'normal');
    const labelWidth = doc.getTextWidth(`${item.label} : `);
    const valueLines = doc.splitTextToSize(item.value, contentWidth - labelWidth - 6);
    doc.text(valueLines, margin + 2 + labelWidth, y);
    y += valueLines.length * 4.5 + 2;
  });
  y += 4;

  // ── Funding badges ──
  doc.setFillColor(...accent);
  ['OPCO', 'FIF-PL', 'CPF', 'Qualiopi'].forEach((badge, i) => {
    const bx = margin + i * 25;
    doc.roundedRect(bx, y, 22, 6, 1, 1, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setCharSpace(0);
    doc.text(badge, bx + 11, y + 4.2, { align: 'center' });
  });
  doc.setTextColor(...navy);
  doc.setFont('helvetica', 'normal');
  y += 14;

  // ── Programme detaille ──
  addSection(fr ? 'Programme detaille' : 'Detailed Program');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);

  training.description.forEach((point, i) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${i + 1}.`, margin + 2, y);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(point, contentWidth - 12);
    doc.text(lines, margin + 10, y);
    y += lines.length * 4.5 + 3;
  });
  y += 4;

  // ── Objectifs pedagogiques ──
  addSection(fr ? 'Objectifs pedagogiques' : 'Learning Objectives');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);

  const objectives = fr ? [
    'Comprendre les fondamentaux de l\'IA generative et ses applications juridiques',
    'Maitriser les outils et techniques adaptes a la pratique du droit',
    'Identifier les risques et mettre en place les bonnes pratiques de securite',
    'Integrer l\'IA dans son quotidien professionnel de maniere ethique et efficace',
  ] : [
    'Understand generative AI fundamentals and legal applications',
    'Master tools and techniques for legal practice',
    'Identify risks and implement security best practices',
    'Integrate AI ethically and efficiently into daily professional work',
  ];

  objectives.forEach(obj => {
    const lines = doc.splitTextToSize(`-  ${obj}`, contentWidth - 4);
    doc.text(lines, margin + 2, y);
    y += lines.length * 4.5 + 2;
  });
  y += 4;

  // ── Methodes pedagogiques ──
  addSection(fr ? 'Methodes pedagogiques' : 'Teaching Methods');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);

  const methods = fr ? [
    'Presentation interactive avec demonstrations en direct',
    'Exercices pratiques sur cas juridiques reels',
    'Temps de questions-reponses avec le formateur',
    'Supports de formation numeriques fournis',
    'Catalogue de prompts juridiques pre-rediges',
  ] : [
    'Interactive presentation with live demonstrations',
    'Hands-on exercises on real legal cases',
    'Q&A time with the instructor',
    'Digital training materials provided',
    'Pre-written legal prompt catalog included',
  ];

  methods.forEach(m => {
    const lines = doc.splitTextToSize(`-  ${m}`, contentWidth - 4);
    doc.text(lines, margin + 2, y);
    y += lines.length * 4.5 + 1.5;
  });
  y += 4;

  // ── Prerequis ──
  addSection(fr ? 'Prerequis' : 'Prerequisites');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);
  const prereqText = fr
    ? 'Aucun prerequis technique. Connaissance du droit recommandee. Un ordinateur avec connexion internet est necessaire.'
    : 'No technical prerequisites. Legal knowledge recommended. A computer with internet access is required.';
  const prereqLines = doc.splitTextToSize(prereqText, contentWidth - 4);
  doc.text(prereqLines, margin + 2, y);
  y += prereqLines.length * 4.5 + 6;

  // ── Modalites d'evaluation ──
  addSection(fr ? 'Evaluation' : 'Assessment');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);
  const evalText = fr
    ? 'Quiz d\'evaluation en fin de session. Attestation de formation delivree sur la base de la participation et de la reussite au quiz.'
    : 'Assessment quiz at end of session. Training certificate issued based on participation and quiz results.';
  const evalLines = doc.splitTextToSize(evalText, contentWidth - 4);
  doc.text(evalLines, margin + 2, y);
  y += evalLines.length * 4.5 + 6;

  // ── Tarification ──
  addSection(fr ? 'Tarification et financement' : 'Pricing and Funding');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);

  const pricingItems = fr ? [
    'Tarif : 199,99 EUR HT par participant',
    'Formation eligible au financement OPCO, FIF-PL et CPF',
    'Devis personnalise sur demande pour les groupes',
    'Possibilite de prise en charge a 100% selon votre OPCO',
    'Convention de formation fournie pour toute inscription',
  ] : [
    'Price: EUR 199.99 excl. VAT per participant',
    'Training eligible for OPCO, FIF-PL and CPF funding',
    'Custom quotes available for groups',
    'Up to 100% coverage possible depending on your OPCO',
    'Training agreement provided upon registration',
  ];

  pricingItems.forEach(p => {
    const lines = doc.splitTextToSize(`-  ${p}`, contentWidth - 4);
    doc.text(lines, margin + 2, y);
    y += lines.length * 4.5 + 1.5;
  });
  y += 6;

  // ── Legal mentions ──
  doc.setDrawColor(203, 213, 225);
  doc.line(margin, y, pageWidth - margin, y);
  y += 5;
  doc.setFontSize(7);
  doc.setTextColor(...muted);
  doc.setCharSpace(0);
  doc.setFont('helvetica', 'bold');
  doc.text('Gaius — Formation a l\'IA juridique', margin, y);
  doc.setFont('helvetica', 'normal');
  y += 4;
  const legalLines = [
    'SAS au capital de 1 000 EUR — SIRET : 934 609 461 00018 — RCS Paris',
    'N. de declaration d\'activite : en cours d\'enregistrement aupres de la DRIEETS Ile-de-France',
    'Siege social : 229 rue Saint-Honore, 75001 Paris',
    'Email : formation@gaius-tech.com — Site : gaius-tech.com',
    fr ? 'Certification Qualiopi — Categorie : Actions de formation' : 'Qualiopi certification — Category: Training actions',
  ];
  legalLines.forEach(line => {
    doc.text(line, margin, y);
    y += 3.5;
  });
  y += 2;
  doc.setFontSize(6.5);
  doc.text(fr ? 'Document genere automatiquement — Programme susceptible de modifications' : 'Automatically generated — Program subject to change', margin, y);

  addPageFooter();

  // Save
  const slug = training.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
  doc.save(`brochure-${slug}.pdf`);
}
